"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import BrainContentPinnedWave from "./BrainContentPinnedWave";
import { usePinnedWaves } from "@/hooks/usePinnedWaves";
import { useRouter, useSearchParams } from "next/navigation";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
const BrainContentPinnedWaves: React.FC = () => {
  const $ = _c(36);
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    pinnedIds,
    addId,
    removeId
  } = usePinnedWaves();
  const {
    isApp
  } = useDeviceInfo();
  const {
    directMessages
  } = useMyStream();
  let t0;
  if ($[0] !== directMessages?.list) {
    t0 = directMessages?.list ?? [];
    $[0] = directMessages?.list;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const directMessagesList = t0;
  const [onHoverWaveId, setOnHoverWaveId] = useState(null);
  const containerRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = direction => {
      const container = containerRef.current;
      if (container) {
        container.scrollBy({
          left: direction === "left" ? -200 : 200,
          behavior: "smooth"
        });
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const scrollHorizontally = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (window.matchMedia("(pointer: coarse)").matches) {
        setShowLeftArrow(false);
        setShowRightArrow(false);
        return;
      }
      const container_0 = containerRef.current;
      if (container_0) {
        const scrollableWidth = container_0.scrollWidth;
        const viewportWidth = container_0.offsetWidth;
        const currentScroll = container_0.scrollLeft;
        const maxScroll = scrollableWidth - viewportWidth;
        const isAtEnd = Math.abs(currentScroll - maxScroll) < 1;
        setShowLeftArrow(currentScroll > 0);
        setShowRightArrow(maxScroll > 0 && !isAtEnd);
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const checkScrollArrows = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const container_1 = containerRef.current;
      if (container_1) {
        checkScrollArrows();
        const timeoutId = setTimeout(checkScrollArrows, 100);
        const mutationObserver = new MutationObserver(checkScrollArrows);
        mutationObserver.observe(container_1, {
          childList: true,
          subtree: true,
          attributes: true
        });
        const resizeObserver = new ResizeObserver(checkScrollArrows);
        resizeObserver.observe(container_1);
        container_1.addEventListener("scroll", checkScrollArrows);
        window.addEventListener("resize", checkScrollArrows);
        const images = container_1.getElementsByTagName("img");
        Array.from(images).forEach(img => {
          img.addEventListener("load", checkScrollArrows);
        });
        return () => {
          clearTimeout(timeoutId);
          mutationObserver.disconnect();
          resizeObserver.disconnect();
          container_1.removeEventListener("scroll", checkScrollArrows);
          window.removeEventListener("resize", checkScrollArrows);
          Array.from(images).forEach(img_0 => {
            img_0.removeEventListener("load", checkScrollArrows);
          });
        };
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== pinnedIds) {
    t4 = [pinnedIds];
    $[5] = pinnedIds;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== addId || $[8] !== searchParams) {
    t5 = () => {
      const wave = searchParams?.get("wave") ?? undefined;
      if (wave && typeof wave === "string") {
        addId(wave);
      }
    };
    t6 = [searchParams, addId];
    $[7] = addId;
    $[8] = searchParams;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (!pinnedIds.length) {
    return null;
  }
  let t7;
  if ($[11] !== directMessagesList || $[12] !== isApp || $[13] !== removeId || $[14] !== router || $[15] !== searchParams) {
    t7 = async waveId => {
      const currentWaveId = searchParams?.get("wave") ?? undefined;
      if (currentWaveId === waveId) {
        const isDirectMessage = directMessagesList.some(w => w.id === waveId);
        router.replace(getWaveHomeRoute({
          isDirectMessage,
          isApp
        }));
      }
      removeId(waveId);
    };
    $[11] = directMessagesList;
    $[12] = isApp;
    $[13] = removeId;
    $[14] = router;
    $[15] = searchParams;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const onRemove = t7;
  let t8;
  if ($[17] !== showLeftArrow) {
    t8 = showLeftArrow && <button ref={leftArrowRef} aria-label="Scroll left" onClick={() => scrollHorizontally("left")} className="tw-inline-flex tw-items-center tw-justify-center tw-group tw-absolute tw-z-10 tw-top-0 sm:tw-top-0.5 tw-p-0 tw-size-8 sm:tw-size-7 tw-left-0 tw-bg-iron-700 tw-ring-1 tw-ring-inset tw-ring-white/10 tw-rounded-md tw-border-none"><svg className="tw-size-5 sm:tw-size-4 tw-text-iron-200 group-hover:tw-text-iron-400 tw-rotate-90 tw-transition tw-duration-300 tw-ease-out" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>;
    $[17] = showLeftArrow;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== showRightArrow) {
    t9 = showRightArrow && <button ref={rightArrowRef} aria-label="Scroll right" onClick={() => scrollHorizontally("right")} className="tw-inline-flex tw-items-center tw-justify-center tw-group tw-absolute tw-z-10 tw-right-0 tw-top-0 sm:tw-top-0.5 tw-p-0 tw-size-8 sm:tw-size-7 tw-bg-iron-700 tw-ring-1 tw-ring-inset tw-ring-white/10 tw-rounded-md tw-border-none"><svg className="tw-size-5 sm:tw-size-4 tw-text-iron-200 group-hover:tw-text-iron-400 -tw-rotate-90 tw-transition tw-duration-300 tw-ease-out" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>;
    $[19] = showRightArrow;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== onHoverWaveId || $[22] !== onRemove || $[23] !== pinnedIds || $[24] !== searchParams) {
    let t11;
    if ($[26] !== onHoverWaveId || $[27] !== onRemove || $[28] !== searchParams) {
      t11 = id => <BrainContentPinnedWave key={id} waveId={id} active={(searchParams?.get("wave") ?? undefined) === id || onHoverWaveId === id} onMouseEnter={setOnHoverWaveId} onMouseLeave={() => setOnHoverWaveId(null)} onRemove={onRemove} />;
      $[26] = onHoverWaveId;
      $[27] = onRemove;
      $[28] = searchParams;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    t10 = pinnedIds.map(t11);
    $[21] = onHoverWaveId;
    $[22] = onRemove;
    $[23] = pinnedIds;
    $[24] = searchParams;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[30] !== t10) {
    t11 = <div ref={containerRef} className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-overflow-x-auto tw-overflow-y-hidden no-scrollbar tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300"><div className="tw-flex tw-gap-x-3">{t10}</div></div>;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t8 || $[34] !== t9) {
    t12 = <div className="tw-relative tw-h-8 tw-mb-2 tw-mr-2">{t8}{t9}{t11}</div>;
    $[32] = t11;
    $[33] = t8;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};
export default BrainContentPinnedWaves;